import React from "react";
import "./ShatterText.css";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const BANDS = 7;

type ShatterTextProps = {
  text: string;
  className?: string;
};

/**
 * Assembles text out of horizontal slices that arrive out of order.
 *
 * This is the fragment-assembly idea from the Alien title applied to ordinary
 * DOM text. Hand-authored stroke letterforms only exist for the hero's fixed
 * string; extending them to a full alphabet would still not survive line
 * wrapping or selection. Slicing the rendered text instead gives the same
 * "pieces locking into place" read on any string, in any font, with the real
 * text still underneath for screen readers.
 *
 * Short display strings only — one copy per band means this is for headings
 * and titles, never body copy.
 */
const ShatterText: React.FC<ShatterTextProps> = ({ text, className = "" }) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [assembled, setAssembled] = React.useState(() => prefersReducedMotion());

  React.useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;

    // Safety net: text must never stay invisible. IntersectionObserver can
    // miss under StrictMode's mount/unmount/remount, and a missed callback
    // here means the string never renders at all. Reveal regardless after a
    // beat — worst case an off-screen element animates early.
    const failsafe = window.setTimeout(() => setAssembled(true), 900);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setAssembled(true);
          observer.disconnect();
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => {
      window.clearTimeout(failsafe);
      observer.disconnect();
    };
  }, []);

  return (
    <span
      ref={ref}
      className={`shatter${assembled ? " shatter--in" : ""} ${className}`.trim()}
      aria-label={text}
    >
      {/* Sizing copy — lays out the box the absolute bands fill. */}
      <span className="shatter__ghost" aria-hidden="true">
        {text}
      </span>

      {Array.from({ length: BANDS }, (_, i) => {
        // Deterministic scatter: bands must not arrive top to bottom, or it
        // reads as a wipe instead of an assembly.
        const scatter = ((i * 2654435761) % 997) / 997;
        const drift = (scatter - 0.5) * 26;
        return (
          <span
            key={i}
            aria-hidden="true"
            className="shatter__band"
            style={
              {
                clipPath: `inset(${(i * 100) / BANDS}% 0 ${
                  100 - ((i + 1) * 100) / BANDS
                }% 0)`,
                animationDelay: `${Math.round(scatter * 380)}ms`,
                "--shatter-drift": `${drift}px`,
              } as React.CSSProperties
            }
          >
            {text}
          </span>
        );
      })}
    </span>
  );
};

export default ShatterText;
